/**
 * Quick View Modal JavaScript
 * Handles modal functionality, product data fetching, and user interactions
 */

class QuickViewModal {
    constructor() {
        this.modal = null;
        this.currentProduct = null;
        this.currentVariant = null;
        this.isOpen = false;
        this.sizeChartModal = null;

        this.init();
    }

    init() {
        this.modal = document.getElementById('quick-view-modal');
        this.sizeChartModal = document.getElementById('quick-view-size-chart');

        if (!this.modal) {
            console.error('Quick view modal not found');
            return;
        }

        this.bindEvents();
        this.setupKeyboardNavigation();
        this.setupSizeChartEvents();
    }

    setupSizeChartEvents() {
        if (!this.sizeChartModal) return;

        // The custom-size-chart already has its own close functionality
        // We just need to handle the modal display
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.sizeChartModal.style.display !== 'none') {
                this.closeSizeChart();
            }
        });
    }

    bindEvents() {
        // Close button
        const closeBtn = this.modal.querySelector('.quick-view-modal__close');
        if (closeBtn) {
            closeBtn.addEventListener('click', () => this.close());
        }

        // Overlay click to close
        const overlay = this.modal.querySelector('.quick-view-modal__overlay');
        if (overlay) {
            overlay.addEventListener('click', () => this.close());
        }

        // Escape key to close
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isOpen) {
                this.close();
            }
        });

        // Add to cart button
        const addToCartBtn = this.modal.querySelector('.quick-view-modal__add-to-cart');
        if (addToCartBtn) {
            addToCartBtn.addEventListener('click', () => this.addToCart());
        }

        // View details link
        const viewDetailsLink = this.modal.querySelector('.quick-view-modal__view-details');
        if (viewDetailsLink) {
            viewDetailsLink.addEventListener('click', (e) => {
                e.preventDefault();
                this.goToProductPage();
            });
        }

        // Size guide link
        const sizeGuideLink = this.modal.querySelector('.quick-view-modal__variant-guide');
        if (sizeGuideLink) {
            sizeGuideLink.addEventListener('click', (e) => {
                e.preventDefault();
                this.openSizeChart();
            });
        }
    }

    setupKeyboardNavigation() {
        // Trap focus within modal when open
        this.modal.addEventListener('keydown', (e) => {
            if (!this.isOpen) return;

            if (e.key === 'Tab') {
                const focusableElements = this.modal.querySelectorAll(
                    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                );
                const firstElement = focusableElements[0];
                const lastElement = focusableElements[focusableElements.length - 1];

                if (e.shiftKey) {
                    if (document.activeElement === firstElement) {
                        e.preventDefault();
                        lastElement.focus();
                    }
                } else {
                    if (document.activeElement === lastElement) {
                        e.preventDefault();
                        firstElement.focus();
                    }
                }
            }
        });
    }

    async open(productHandle) {
        if (this.isOpen) return;

        try {
            this.showLoadingState();
            this.modal.classList.add('is-open');
            this.isOpen = true;

            // Focus management
            this.modal.setAttribute('aria-hidden', 'false');
            this.modal.querySelector('.quick-view-modal__close').focus();

            // Fetch product data
            await this.fetchProductData(productHandle);

        } catch (error) {
            console.error('Error opening quick view modal:', error);
            this.showErrorState();
        }
    }

    close() {
        if (!this.isOpen) return;

        this.modal.classList.remove('is-open');
        this.isOpen = false;

        // Focus management
        this.modal.setAttribute('aria-hidden', 'true');

        // Reset modal state
        setTimeout(() => {
            this.resetModalState();
        }, 300); // Wait for close animation
    }

    showLoadingState() {
        const loading = this.modal.querySelector('.quick-view-modal__loading');
        const productContent = this.modal.querySelector('.quick-view-modal__product-content');

        if (loading) loading.style.display = 'block';
        if (productContent) productContent.style.display = 'none';
    }

    showErrorState() {
        const loading = this.modal.querySelector('.quick-view-modal__loading');
        if (loading) {
            loading.innerHTML = `
        <div class="quick-view-modal__error">
          <p>Error loading product. Please try again.</p>
          <button class="quick-view-modal__retry-btn" onclick="location.reload()">Retry</button>
        </div>
      `;
        }
    }

    resetModalState() {
        const loading = this.modal.querySelector('.quick-view-modal__loading');
        const productContent = this.modal.querySelector('.quick-view-modal__product-content');

        if (loading) {
            loading.style.display = 'block';
            loading.innerHTML = `
        <div class="quick-view-modal__loading-spinner"></div>
        <p>Loading product details...</p>
      `;
        }

        if (productContent) {
            productContent.style.display = 'none';
            productContent.classList.remove('is-loaded');
        }

        this.currentProduct = null;
        this.currentVariant = null;
    }

    async fetchProductData(productHandle) {
        try {
            const response = await fetch(`/products/${productHandle}.js`);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const product = await response.json();
            this.currentProduct = product;
            this.currentVariant = product.variants[0]; // Default to first variant

            this.populateModal(product);

        } catch (error) {
            console.error('Error fetching product data:', error);
            throw error;
        }
    }

    populateModal(product) {
        const loading = this.modal.querySelector('.quick-view-modal__loading');
        const productContent = this.modal.querySelector('.quick-view-modal__product-content');

        // Hide loading, show content
        if (loading) loading.style.display = 'none';
        if (productContent) {
            productContent.style.display = 'block';

            // Populate product information
            this.populateBasicInfo(product);
            this.populateImages(product);
            this.populateVariants(product);
            this.populatePricing(product);

            // Set initial variant ID for pickup availability
            const pickupAvailability = this.modal.querySelector('.product__pickup-availabilities');
            if (pickupAvailability && this.currentVariant) {
                pickupAvailability.setAttribute('data-variant-id', this.currentVariant.id);
            }

            // Fetch and populate pickup availability
            this.populatePickupAvailability(product);

            // Show content with animation
            setTimeout(() => {
                productContent.classList.add('is-loaded');
            }, 100);
        }
    }

    populateBasicInfo(product) {
        // Brand name
        const brandName = this.modal.querySelector('.quick-view-modal__brand-name');
        if (brandName) {
            brandName.textContent = product.vendor || '';
        }

        // Product title
        const title = this.modal.querySelector('.quick-view-modal__title');
        if (title) {
            title.textContent = product.title || '';
        }

        // Description
        const description = this.modal.querySelector('.quick-view-modal__description');
        if (description) {
            const desc = product.description || '';
            // Show only first paragraph for quick view
            const firstParagraph = desc.split('\n\n')[0] || desc;
            // Use innerHTML to preserve HTML formatting (Shopify data is safe)
            description.innerHTML = firstParagraph;
        }

        // View details link
        const viewDetailsLink = this.modal.querySelector('.quick-view-modal__view-details');
        if (viewDetailsLink) {
            viewDetailsLink.href = `/products/${product.handle}`;
        }
    }

    populateImages(product) {
        const imageGallery = this.modal.querySelector('.quick-view-modal__image-gallery');
        if (!imageGallery) return;

        imageGallery.innerHTML = '';

        // Handle both media array and images array
        let imagesToShow = [];

        if (product.media && product.media.length > 0) {
            // Use media array if available (newer Shopify format)
            imagesToShow = product.media.slice(0, 3).map(media => ({
                src: media.preview_image ? media.preview_image.src : media.src,
                alt: media.alt || `${product.title} - Image`
            }));
        } else if (product.images && product.images.length > 0) {
            // Fallback to images array (older format)
            imagesToShow = product.images.slice(0, 3).map((image, index) => ({
                src: image,
                alt: `${product.title} - Image ${index + 1}`
            }));
        }

        imagesToShow.forEach((imageData, index) => {
            const imageItem = document.createElement('div');
            imageItem.className = 'quick-view-modal__image-item';

            const img = document.createElement('img');
            img.src = imageData.src;
            img.alt = imageData.alt;
            img.loading = 'lazy';

            imageItem.appendChild(img);
            imageGallery.appendChild(imageItem);
        });
    }

    populateVariants(product) {
        const variantOptions = this.modal.querySelector('.quick-view-modal__variant-options');
        if (!variantOptions) return;

        variantOptions.innerHTML = '';

        // Find the first option that has multiple values (usually size, color, etc.)
        const optionToShow = product.options.find(option =>
            option.values.length > 1
        );

        if (optionToShow) {
            // Update the label to match the actual option name
            const variantLabel = this.modal.querySelector('.quick-view-modal__variant-label');
            if (variantLabel) {
                variantLabel.textContent = optionToShow.name.toUpperCase();
            }

            optionToShow.values.forEach((value, index) => {
                const optionBtn = document.createElement('button');
                optionBtn.className = 'quick-view-modal__variant-option';
                optionBtn.textContent = value;
                optionBtn.type = 'button';

                // Find the variant that matches this option value
                let variant;
                if (optionToShow.position === 1) {
                    variant = product.variants.find(v => v.option1 === value);
                } else if (optionToShow.position === 2) {
                    variant = product.variants.find(v => v.option2 === value);
                } else if (optionToShow.position === 3) {
                    variant = product.variants.find(v => v.option3 === value);
                }

                if (variant && !variant.available) {
                    optionBtn.classList.add('is-unavailable');
                    optionBtn.disabled = true;
                }

                // Set first available variant as selected
                if (index === 0 && variant && variant.available) {
                    optionBtn.classList.add('is-selected');
                    this.currentVariant = variant;
                }

                optionBtn.addEventListener('click', () => this.selectVariant(variant, optionBtn));
                variantOptions.appendChild(optionBtn);
            });

            // Check if size chart should be shown for this product
            this.checkSizeChartVisibility(product);
        }
    }

    checkSizeChartVisibility(product) {
        const sizeGuideLink = this.modal.querySelector('.quick-view-modal__variant-guide');
        if (!sizeGuideLink) return;

        // Check product tags to determine if size chart should be shown
        let shouldShowSizeChart = false;
        let forMen = false;
        let forMen2 = false;
        let forMen3 = false;
        let forMen4 = false;

        if (product.tags && product.tags.length > 0) {
            product.tags.forEach(tag => {
                const tagHandle = tag.toLowerCase().replace(/\s+/g, '-');
                if (tagHandle === 'men-shoes') {
                    forMen = true;
                } else if (tagHandle === 'women-shoes') {
                    forMen2 = true;
                } else if (tagHandle === 'men-clothing') {
                    forMen3 = true;
                } else if (tagHandle === 'women-clothing') {
                    forMen4 = true;
                }
            });

            shouldShowSizeChart = forMen || forMen2 || forMen3 || forMen4;
        }

        // Show/hide the FIND MY SIZE link based on the condition
        if (shouldShowSizeChart) {
            sizeGuideLink.style.display = 'inline-flex';
        } else {
            sizeGuideLink.style.display = 'none';
        }
    }

    openSizeChart() {
        if (!this.sizeChartModal || !this.currentProduct) return;

        // Fetch size chart content using section rendering API
        this.fetchSizeChartContent();

        // Show the size chart modal and add active class
        this.sizeChartModal.style.display = 'block';
        this.sizeChartModal.classList.add('active');
    }

    closeSizeChart() {
        if (!this.sizeChartModal) return;

        // Remove active class and hide modal
        this.sizeChartModal.classList.remove('active');
        this.sizeChartModal.style.display = 'none';

        // Return focus to the FIND MY SIZE link
        const sizeGuideLink = this.modal.querySelector('.quick-view-modal__variant-guide');
        if (sizeGuideLink) {
            sizeGuideLink.focus();
        }
    }

    async fetchSizeChartContent() {
        try {
            // Use Shopify section rendering API to get the size chart content
            // This will render the same section as the main product page
            const response = await fetch(`/products/${this.currentProduct.handle}?sections=main-product`);

            if (!response.ok) {
                throw new Error('Failed to fetch product section');
            }

            const sectionsData = await response.json();
            const mainProductSection = sectionsData['main-product'];

            if (mainProductSection) {
                // Parse the HTML to extract the size chart content
                const parser = new DOMParser();
                const doc = parser.parseFromString(mainProductSection, 'text/html');

                // Find the size chart section in the rendered HTML
                const sizeChartSection = doc.querySelector('.custom-size-chart');

                if (sizeChartSection) {
                    // Replace the entire content with the fetched size chart
                    this.sizeChartModal.innerHTML = sizeChartSection.innerHTML;

                    // Re-query for the close button since we replaced the HTML
                    const closeBtn = this.sizeChartModal.querySelector('.close_size-chart');
                    if (closeBtn) {
                        closeBtn.addEventListener('click', () => this.closeSizeChart());
                    }
                } else {
                    this.showDefaultSizeChartContent();
                }
            } else {
                this.showDefaultSizeChartContent();
            }
        } catch (error) {
            console.error('Error fetching size chart content:', error);
            this.showDefaultSizeChartContent();
        }
    }

    showDefaultSizeChartContent() {
        if (!this.sizeChartModal) return;

        this.sizeChartModal.innerHTML = `
            <div class="custom-size-chart-inner">
                <span class="close_size-chart" onclick="this.closest('.custom-size-chart').style.display='none'"> x </span>
                <div class="size-chart-wrap content container">
                    <div class="size-chart-wrap-inner">
                        <div class="size-chart-default">
                            <p>Size guide information is not available for this product.</p>
                            <p>Please contact customer service for sizing assistance.</p>
                        </div>
                    </div>
                </div>
            </div>
        `;

        // Add event listener to the close button
        const closeBtn = this.sizeChartModal.querySelector('.close_size-chart');
        if (closeBtn) {
            closeBtn.addEventListener('click', () => this.closeSizeChart());
        }
    }

    populatePricing(product) {
        // Price
        const priceElement = this.modal.querySelector('.quick-view-modal__price');
        if (priceElement) {
            const price = this.currentVariant ? this.currentVariant.price : product.price;
            priceElement.textContent = this.formatPrice(price);
        }

        // Points
        const pointsValue = this.modal.querySelector('.quick-view-modal__points-value');
        if (pointsValue) {
            const price = this.currentVariant ? this.currentVariant.price : product.price;
            const points = Math.floor(price / 100); // Assuming 1 point per dollar
            pointsValue.textContent = points;
        }
    }

    selectVariant(variant, buttonElement) {
        if (!variant || !variant.available) return;

        // Update selected state
        const allVariantOptions = this.modal.querySelectorAll('.quick-view-modal__variant-option');
        allVariantOptions.forEach(btn => btn.classList.remove('is-selected'));
        buttonElement.classList.add('is-selected');

        // Update current variant
        this.currentVariant = variant;

        // Update pricing
        this.updatePricing(variant);

        // Update images if variant has specific images
        if (variant.featured_image) {
            this.updateVariantImage(variant.featured_image);
        }

        // Update pickup availability for the new variant
        if (this.currentProduct) {
            this.populatePickupAvailability(this.currentProduct);
        }

        // Update the variant ID in the pickup availability element
        const pickupAvailability = this.modal.querySelector('.product__pickup-availabilities');
        if (pickupAvailability) {
            pickupAvailability.setAttribute('data-variant-id', variant.id);
        }
    }

    updatePricing(variant) {
        const priceElement = this.modal.querySelector('.quick-view-modal__price');
        if (priceElement) {
            priceElement.textContent = this.formatPrice(variant.price);
        }

        const pointsValue = this.modal.querySelector('.quick-view-modal__points-value');
        if (pointsValue) {
            const points = Math.floor(variant.price / 100);
            pointsValue.textContent = points;
        }
    }

    updateVariantImage(imageUrl) {
        const firstImageItem = this.modal.querySelector('.quick-view-modal__image-item img');
        if (firstImageItem) {
            firstImageItem.src = imageUrl;
        }
    }

    async populatePickupAvailability(product) {
        try {
            // Get the current variant for pickup availability
            const variant = this.currentVariant || product.variants[0];
            if (!variant) return;

            // Use Shopify's pickup availability endpoint
            const response = await fetch(`/variants/${variant.id}/?section_id=pickup-availability`);

            if (!response.ok) {
                throw new Error('Failed to fetch pickup availability');
            }

            const htmlResponse = await response.text();

            // Find the existing pickup availability element in the modal
            const modalPickupAvailability = this.modal.querySelector('.product__pickup-availabilities');

            if (modalPickupAvailability) {
                // Update the variant ID
                modalPickupAvailability.setAttribute('data-variant-id', variant.id);

                // Parse the HTML response to extract content
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = htmlResponse;

                // Extract pickup-availability-preview content
                const previewContent = tempDiv.querySelector('pickup-availability-preview');

                // Extract pickup-availability-drawer first
                const drawer = tempDiv.querySelector('pickup-availability-drawer');

                if (previewContent) {
                    modalPickupAvailability.innerHTML = previewContent.outerHTML;

                    // Add event listener to the "Check availability at other stores" button
                    const checkAvailabilityButton = modalPickupAvailability.querySelector('#ShowPickupAvailabilityDrawer');
                    if (checkAvailabilityButton && drawer) {
                        checkAvailabilityButton.addEventListener('click', (evt) => {
                            this.showPickupAvailabilityDrawer(drawer, evt.target);
                        });
                    }
                }

                // Append pickup-availability-drawer to body
                if (drawer) {
                    // Remove existing drawer if it exists
                    const existingDrawer = document.querySelector('pickup-availability-drawer');
                    if (existingDrawer) {
                        existingDrawer.remove();
                    }

                    // Append new drawer to body
                    document.body.appendChild(drawer);

                    // Add event listeners similar to pickup-availability.js
                    this.setupPickupAvailabilityDrawer(drawer);
                }

                // Show the pickup section
                const pickupSection = this.modal.querySelector('.quick-view-modal__pickup-section');
                if (pickupSection) {
                    pickupSection.style.display = 'block';
                }
            }

        } catch (error) {
            console.error('Error fetching pickup availability:', error);
            this.showDefaultPickupMessage();
        }
    }



    showDefaultPickupMessage() {
        const pickupSection = this.modal.querySelector('.quick-view-modal__pickup-section');
        const pickupInfo = this.modal.querySelector('.quick-view-modal__pickup-info');
        const pickupText = this.modal.querySelector('.quick-view-modal__pickup-text');
        const pickupIcon = this.modal.querySelector('.quick-view-modal__pickup-icon');
        const pickupLink = this.modal.querySelector('.quick-view-modal__pickup-link');

        if (!pickupSection || !pickupInfo || !pickupText || !pickupIcon) return;

        pickupSection.setAttribute('data-pickup-status', 'loading');
        pickupIcon.textContent = '?';
        pickupIcon.className = 'quick-view-modal__pickup-icon pickup-loading';
        pickupText.textContent = 'Pickup availability loading...';
        pickupLink.textContent = 'Check availability';
        pickupLink.href = '#';
        pickupSection.style.display = 'flex';
    }

    showAllPickupLocations(pickupLocations) {
        // Create a simple modal or expandable section to show all locations
        const locationsList = pickupLocations.map(location => {
            const readyTime = location.pickup_time || '24 hours';
            return `${location.name} - Ready in ${readyTime}`;
        }).join('\n');

        // For now, just show in console. You could create a more sophisticated UI here
        console.log('All pickup locations:', locationsList);

        // You could also create a tooltip or expand the pickup section to show all locations
        alert(`Available pickup locations:\n\n${locationsList}`);
    }

    formatPrice(price) {
        // Convert cents to dollars and format
        const dollars = price / 100;
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(dollars);
    }

    async addToCart() {
        if (!this.currentVariant) {
            console.error('No variant selected');
            return;
        }

        try {
            const addToCartBtn = this.modal.querySelector('.quick-view-modal__add-to-cart');
            const originalText = addToCartBtn.textContent;

            // Show loading state
            addToCartBtn.textContent = 'Adding...';
            addToCartBtn.disabled = true;

            // Add to cart using Shopify's AJAX API
            const response = await fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    id: this.currentVariant.id,
                    quantity: 1
                })
            });

            if (!response.ok) {
                throw new Error('Failed to add to cart');
            }

            // Success - update cart count if cart count element exists
            this.updateCartCount();

            // Reset button state first
            const cartButton = this.modal.querySelector('.quick-view-modal__add-to-cart');
            if (cartButton) {
                cartButton.textContent = 'ADD TO CART';
                cartButton.disabled = false;
            }

            // Open cart drawer instead of showing notification
            await this.openCartDrawer();

            // Close modal
            this.close();

        } catch (error) {
            console.error('Error adding to cart:', error);
            this.showErrorMessage('Failed to add to cart. Please try again.');
        } finally {
            // Reset button state
            const addToCartBtn = this.modal.querySelector('.quick-view-modal__add-to-cart');
            addToCartBtn.textContent = originalText;
            addToCartBtn.disabled = false;
        }
    }

    goToProductPage() {
        if (this.currentProduct) {
            window.location.href = `/products/${this.currentProduct.handle}`;
        }
    }

    updateCartCount() {
        // Update cart count in header if cart count element exists
        const cartCountElements = document.querySelectorAll('[data-cart-count]');
        cartCountElements.forEach(element => {
            // Fetch current cart count and update
            fetch('/cart.js')
                .then(response => response.json())
                .then(cart => {
                    element.textContent = cart.item_count;
                })
                .catch(error => console.error('Error updating cart count:', error));
        });
    }

    async openCartDrawer() {
        try {
            // Step 1: Fetch updated cart sections after adding product
            const sectionsResponse = await fetch('/cart?sections=cart-drawer,cart-icon-bubble');

            if (!sectionsResponse.ok) {
                throw new Error('Failed to load updated cart sections');
            }

            const sectionsJSON = await sectionsResponse.json();

            // Step 2: Render into Dawn's cart drawer
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
                const sectionsToRender = {
                    sections: sectionsJSON,
                    sections_url: '/cart'
                };
                cartDrawer.renderContents(sectionsToRender);
                // Remove is-empty class from cart-drawer element
                cartDrawer.classList.remove('is-empty');
                cartDrawer.open();
            } else {
                // Fallback: redirect to cart page
                window.location.href = '/cart';
            }
        } catch (error) {
            console.error('Error updating cart drawer:', error);
            // Fallback: redirect to cart page
            window.location.href = '/cart';
        }
    }

    setupPickupAvailabilityDrawer(drawer) {
        // Add event listeners similar to pickup-availability.js

        // Close button event listener
        const closeButton = drawer.querySelector('.pickup-availability-drawer-button');
        if (closeButton) {
            closeButton.addEventListener('click', () => {
                this.hidePickupAvailabilityDrawer(drawer);
            });
        }

        // Escape key event listener
        drawer.addEventListener('keyup', (event) => {
            if (event.code.toUpperCase() === 'ESCAPE') {
                this.hidePickupAvailabilityDrawer(drawer);
            }
        });

        // Body click event listener
        const onBodyClick = (evt) => {
            const target = evt.target;
            if (target !== drawer && !target.closest('pickup-availability-drawer') && target.id !== 'ShowPickupAvailabilityDrawer') {
                this.hidePickupAvailabilityDrawer(drawer);
            }
        };

        // Store the event listener reference for removal
        drawer._onBodyClick = onBodyClick;
        document.body.addEventListener('click', onBodyClick);
    }

    hidePickupAvailabilityDrawer(drawer) {
        drawer.removeAttribute('open');
        document.body.removeEventListener('click', drawer._onBodyClick);
        document.body.classList.remove('overflow-hidden');
        // Note: removeTrapFocus would need to be implemented if needed
    }

    showPickupAvailabilityDrawer(drawer, focusElement) {
        drawer.focusElement = focusElement;
        drawer.setAttribute('open', '');
        document.body.classList.add('overflow-hidden');
        // Note: trapFocus would need to be implemented if needed
    }

    showSuccessMessage(message) {
        this.showMessage(message, 'success');
    }

    showErrorMessage(message) {
        this.showMessage(message, 'error');
    }

    showMessage(message, type) {
        // Create and show a temporary message
        const messageElement = document.createElement('div');
        messageElement.className = `quick-view-modal__message quick-view-modal__message--${type}`;
        messageElement.textContent = message;

        this.modal.appendChild(messageElement);

        // Remove message after delay
        setTimeout(() => {
            if (messageElement.parentNode) {
                messageElement.parentNode.removeChild(messageElement);
            }
        }, 3000);
    }
}

// Initialize Quick View Modal
document.addEventListener('DOMContentLoaded', () => {
    window.quickViewModal = new QuickViewModal();
});

// Global function to open quick view (can be called from collection template)
window.openQuickView = function (productHandle) {
    if (window.quickViewModal) {
        window.quickViewModal.open(productHandle);
    }
};

// Add quick view buttons to product cards
document.addEventListener('DOMContentLoaded', () => {
    // Find all product cards and add quick view buttons
    const productCards = document.querySelectorAll('.product-card, .grid-product, [data-product-handle]');

    productCards.forEach(card => {
        const productHandle = card.dataset.productHandle ||
            card.querySelector('[data-product-handle]')?.dataset.productHandle ||
            card.querySelector('a[href*="/products/"]')?.href.split('/products/')[1]?.split('?')[0];

        if (productHandle) {
            // Check if quick view button already exists
            if (!card.querySelector('.quick-view-btn')) {
                const quickViewBtn = document.createElement('button');
                quickViewBtn.className = 'quick-view-btn';
                quickViewBtn.textContent = 'Quick View';
                quickViewBtn.type = 'button';
                quickViewBtn.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    window.openQuickView(productHandle);
                });

                // Insert button into product card
                const actionsContainer = card.querySelector('.product-card__actions, .grid-product__actions, .product-actions') || card;
                actionsContainer.appendChild(quickViewBtn);
            }
        }
    });
});
